import { BrowserRouter as Router, Routes, Route, useLocation } from 'react-router-dom';
import { useEffect } from 'react';

import Dashboard from './Views/Dashboard/Dashboard';
import DynamicNavbar from './components/Navbar/DynamicNavbar';
import LandingPage from './Views/LandingPage/LandingPage';
import ProjectView from './Views/ProjectView/ProjectView';
import Settings from './Views/Settings/Settings';
import urls from './urls';
import Footer from './components/Footer/Footer';
import PrivacyPolicy from './components/Privacy/PrivacyPolicy';

import { ThemeProvider } from './contexts/ThemeContext';
import useThemeStyles from './hooks/useThemeStyles';
import offlineManager from './utils/apiRequests/offlineManager';
import {
  CommandPaletteProvider,
  CommandPalette,
  GlobalCommands,
} from './components/CommandPalette';

const AppContent = () => {
  const { colors } = useThemeStyles();

  // Initialize offline manager on mount
  useEffect(() => {
    offlineManager.init();

    // Cleanup on unmount
    return () => {
      offlineManager.cleanup();
    };
  }, []);

  // Apply theme background to html and body elements
  useEffect(() => {
    document.documentElement.style.backgroundColor = colors.background.primary;
    document.documentElement.style.color = colors.text.primary;
    document.documentElement.style.transition = 'background-color 0.3s ease, color 0.3s ease';

    document.body.style.backgroundColor = colors.background.primary;
    document.body.style.color = colors.text.primary;
    document.body.style.transition = 'background-color 0.3s ease, color 0.3s ease';
  }, [colors.background.primary, colors.text.primary]);

  const appStyle = {
    display: 'flex',
    flexDirection: 'column',
    minHeight: '100vh',
    backgroundColor: colors.background.primary,
    color: colors.text.primary,
    transition: 'background-color 0.3s ease, color 0.3s ease',
  };

  return (
    <div className="App" style={appStyle}>
      <Router>
        <CommandPaletteProvider>
          <GlobalCommands />
          <AppRouter />
          <CommandPalette />
        </CommandPaletteProvider>
      </Router>
    </div>
  );
};

const AppRouter = () => {
  const location = useLocation();

  // Determine if footer should be shown based on current route
  const shouldShowFooter = location.pathname === urls.base || location.pathname === urls.dashboard;

  // Allow scrolling on landing page
  // For project view and other app pages, individual columns should scroll independently
  const isLandingPage = location.pathname === urls.base;

  // Landing page: allow scrolling on the main container
  // Project view: use flex sizing, let individual components handle scrolling
  // Dashboard: allow natural growth so footer can be at bottom
  const isDashboard = location.pathname === urls.dashboard;
  let contentStyle;
  if (isLandingPage) {
    contentStyle = { flex: 1, overflow: 'auto', height: 'auto' };
  } else if (isDashboard) {
    contentStyle = { flex: 1, display: 'flex', flexDirection: 'column' };
  } else {
    contentStyle = {
      flex: '1 1 0',
      overflow: 'hidden',
      display: 'flex',
      flexDirection: 'column',
      minHeight: 0,
    };
  }

  return (
    <>
      <DynamicNavbar />
      <div style={contentStyle}>
        <Routes>
          <Route path={urls.base} element={<LandingPage />} />
          <Route path={urls.dashboard} element={<Dashboard />} />
          <Route path={`${urls.projectDetails}/*`} element={<ProjectView />} />
          <Route path={urls.privacy} element={<PrivacyPolicy />} />
          <Route path={urls.settings} element={<Settings />} />
          <Route path="*" element={<LandingPage />} />
        </Routes>
      </div>
      {shouldShowFooter && <Footer />}
    </>
  );
};

const App = () => {
  return (
    <ThemeProvider>
      <AppContent />
    </ThemeProvider>
  );
};

export default App;
